/**
 * Helper utilities for User Profile Config settings and filters.
 */ import {useUserProfileModule} from "@/state/pinia";



/**
 * Builds a single user profile setting DTO.
 *
 * @param {string} settingKey - unique key for the setting (e.g. "TABLE_PAGE_SIZE", "VISIBLE_COLUMNS")
 * @param {string} name - human-readable name
 * @param {string|null} value - serialized value (JSON string, number string, etc.)
 * @param {string} view - the view key this setting belongs to
 * @param {Object} [options] - optional overrides
 * @param {number|null} [options.id] - existing setting id for updates
 * @param {string|null} [options.valueType] - type hint (e.g. "STRING", "JSON", "NUMBER", "BOOLEAN")
 * @param {boolean} [options.requireNotEmpty] - whether the value must be non-empty
 * @param {boolean} [options.visible] - whether the setting is visible to the user
 * @param {boolean} [options.local] - whether the setting is local-only
 * @param {string|null} [options.context] - optional context key
 * @returns {Object} setting DTO
 */
export function buildUserProfileSettingDto(settingKey, name, value, view, options = {}) {
    return {
        id: options.id ?? null,
        settingKey: settingKey,
        name: name,
        value: value ?? null,
        valueType: options.valueType ?? null,
        requireNotEmpty: options.requireNotEmpty ?? false,
        visible: options.visible ?? true,
        local: options.local ?? false,
        view: view,
        context: options.context ?? null,
        createdAt: null,
        updatedAt: null,
    };
}

/**
 * Builds a setting DTO for visible columns.
 * @param {Array<string>} columnDefinitions - array of visible column definitions
 * @param {string} view - view key
 * @param {string|null} [context] - optional context
 * @param {number|null} [existingId] - existing setting id for updates
 * @returns {Object} setting DTO
 */
export function buildVisibleColumnsSetting(columnDefinitions, view, context = null, existingId = null) {
    return buildUserProfileSettingDto(
        "VISIBLE_COLUMNS",
        "Widoczne kolumny w tabeli",
        JSON.stringify(columnDefinitions),
        view,
        { valueType: "COLLECTION", context, id: existingId }
    );
}

/**
 * Builds a setting DTO for visible columns in grouped mode.
 * @param columnDefinitions
 * @param view
 * @param context
 * @param existingId
 * @returns {{id, settingKey: string, name: string, value, valueType, requireNotEmpty, visible, local, view: string, context, createdAt: null, updatedAt: null}}
 */
export function buildVisibleColumnsGroupedSetting(columnDefinitions, view, context = null, existingId = null) {
    return buildUserProfileSettingDto(
        "VISIBLE_COLUMNS_GROUPED",
        "Widoczne kolumny w widoku grupowanym tabeli",
        JSON.stringify(columnDefinitions),
        view,
        { valueType: "COLLECTION", context, id: existingId }
    );
}

/**
 * Builds a setting DTO for visible columns in grouped-grid mode.
 * @param columnDefinitions
 * @param view
 * @param context
 * @param existingId
 * @returns {{id, settingKey: string, name: string, value, valueType, requireNotEmpty, visible, local, view: string, context, createdAt: null, updatedAt: null}}
 */
export function buildVisibleColumnsGroupedGridSetting(columnDefinitions, view, context = null, existingId = null) {
    return buildUserProfileSettingDto(
        "VISIBLE_COLUMNS_GROUPED_GRID",
        "Widoczne kolumny w widoku grupowanym tabeli z układem siatki",
        JSON.stringify(columnDefinitions),
        view,
        { valueType: "COLLECTION", context, id: existingId }
    );
}

/**
 * Builds a setting DTO for table page size.
 * @param {number} pageSize
 * @param {string} view
 * @param {string|null} [context]
 * @param {number|null} [existingId]
 * @returns {Object} setting DTO
 */
export function buildPageSizeSetting(pageSize, view, context = null, existingId = null) {
    return buildUserProfileSettingDto(
        "TABLE_PAGE_SIZE",
        "Wielkość strony tabeli",
        String(pageSize),
        view,
        { valueType: "NUMBER", context, id: existingId }
    );
}

/**
 * Builds a setting DTO for table sorting.
 * @param {Object} sorting - e.g. { column: "name", direction: "asc" }
 * @param {string} view
 * @param {string|null} [context]
 * @param {number|null} [existingId]
 * @returns {Object} setting DTO
 */
export function buildSortingSetting(sorting, view, context = null, existingId = null) {
    return buildUserProfileSettingDto(
        "TABLE_SORTING",
        "Ustawienia sortowania tabeli",
        JSON.stringify(sorting),
        view,
        { valueType: "STRING", context, id: existingId }
    );
}

export function buildPackageOrderGroupingViewSetting(grouping, view, context = null, existingId = null) {
    return buildUserProfileSettingDto(
        "PACKAGE_ORDER_ITEMS_GROUPING_VIEW",
        "Ustawienia grupowania w widoku generatora modułu zamówień Centralnych",
        JSON.stringify(grouping),
        view,
        { valueType: "STRING", context, id: existingId }
    );
}

/**
 * Finds a setting by key from a settings array.
 * @param {Array} settings - array of setting DTOs
 * @param {string} settingKey
 * @returns {Object|null}
 */
export function findSettingByKey(settings, settingKey) {
    if (!settings || !Array.isArray(settings)) return null;
    return settings.find(s => s.settingKey === settingKey) || null;
}

/**
 * Parses a JSON setting value, returning defaultValue on failure.
 * @param {Object|null} setting - setting DTO
 * @param {*} defaultValue
 * @returns {*}
 */
export function parseJsonSettingValue(setting, defaultValue = null) {
    if (!setting || !setting.value) return defaultValue;
    try {
        return JSON.parse(setting.value);
    } catch (e) {
        return defaultValue;
    }
}

/**
 * Merges a new or updated setting into an existing settings array by settingKey.
 * If a setting with the same key exists, it is replaced (preserving the existing id).
 * If not, the new setting is appended.
 * @param {Array} existingSettings
 * @param {Object} newSetting
 * @returns {Array} updated settings array (new reference)
 */
export function mergeSettingIntoArray(existingSettings, newSetting) {
    const arr = existingSettings ? [...existingSettings] : [];
    const index = arr.findIndex(s => s.settingKey === newSetting.settingKey);
    if (index !== -1) {
        // Preserve existing id if the new one doesn't have it
        const merged = { ...newSetting };
        if (merged.id == null && arr[index].id != null) {
            merged.id = arr[index].id;
        }
        arr[index] = merged;
    } else {
        arr.push(newSetting);
    }
    return arr;
}

/**
 * Resolve currently logged user email using userProfileModule.
 */
export async function resolveCurrentUserEmail() {
    const userProfileModule = useUserProfileModule();
    let email = userProfileModule?.profile?.email;

    if (!email) {
        const myProfile = await userProfileModule.httpFetchMyProfile();
        email = myProfile?.email;
    }

    if (!email) {
        throw new Error("Could not resolve current logged user email.");
    }

    return email;
}

/**
 * Generic loader for user profile view config.
 *
 * Arguments:
 * - userProfileModule
 * - viewKey
 * - context
 * - onLoadingChange(boolean) optional
 * - onConfigLoaded(config) optional
 * - onSettingsLoaded(settings) optional
 * - onFiltersLoaded(savedFilters) optional
 */
export async function loadUserProfileViewConfigRuntime({
                                                           viewKey,
                                                           context = null,
                                                           onLoadingChange = null,
                                                           onConfigLoaded = null,
                                                           onSettingsLoaded = null,
                                                           onFiltersLoaded = null,
                                                       }) {
    const userProfileModule = useUserProfileModule();
    if (onLoadingChange) {
        onLoadingChange(true);
    }

    try {
        const email = await resolveCurrentUserEmail();

        const viewConfig = await userProfileModule.httpGetUserProfileViewConfig(
            email,
            viewKey,
            context
        );

        if (onConfigLoaded) {
            onConfigLoaded(viewConfig?.config || null);
        }

        if (onSettingsLoaded) {
            onSettingsLoaded(viewConfig?.settings || []);
        }

        if (onFiltersLoaded) {
            onFiltersLoaded(viewConfig?.savedFilters || []);
        }

        return viewConfig;
    } finally {
        if (onLoadingChange) {
            onLoadingChange(false);
        }
    }
}

/**
 * Apply common table-related user settings into component state.
 *
 * Returns normalized applied values as object.
 */
export function applyTableUserProfileSettings({
                                                  settings = [],
                                                  fallbackVisibleColumns = [],
                                                  fallbackSortingKey = null,
                                                  fallbackSortingDirection = "ASC",
                                                  tableViewType = "DEFAULT"
                                              }) {
    let visibleColumnsSetting = null;
    if(tableViewType === "GROUPED"){
        visibleColumnsSetting = findSettingByKey(settings, "VISIBLE_COLUMNS_GROUPED");
    }else if(tableViewType === "GROUPED_GRID"){
        visibleColumnsSetting = findSettingByKey(settings, "VISIBLE_COLUMNS_GROUPED_GRID");
    }else {
        visibleColumnsSetting = findSettingByKey(settings, "VISIBLE_COLUMNS");
    }



    const sortingSetting = findSettingByKey(settings, "TABLE_SORTING");

    let visibleColumns = Array.isArray(fallbackVisibleColumns)
        ? [...fallbackVisibleColumns]
        : [];

    let sortingKey = fallbackSortingKey;
    let sortingDirection = fallbackSortingDirection;
    console.log("visibleColumnsSetting:", visibleColumnsSetting)
    const parsedVisibleColumns = parseJsonSettingValue(visibleColumnsSetting, null);
    if (Array.isArray(parsedVisibleColumns) && parsedVisibleColumns.length > 0) {
        visibleColumns = [...parsedVisibleColumns];
    }

    const parsedSorting = parseJsonSettingValue(sortingSetting, null);
    if (parsedSorting && typeof parsedSorting === "object") {
        if (parsedSorting.column) {
            sortingKey = parsedSorting.column;
        }
        if (parsedSorting.direction) {
            sortingDirection = String(parsedSorting.direction).toUpperCase();
        }
    }

    return {
        visibleColumns,
        sortingKey,
        sortingDirection,
    };
}

/**
 * Save full settings array using userProfileModule.
 */
export async function saveUserProfileSettingsRuntime({
                                                         viewKey,
                                                         context = null,
                                                         settingsArray = [],
                                                     }) {
    const userProfileModule = useUserProfileModule();
    const email = await resolveCurrentUserEmail();

    const saved = await userProfileModule.httpSaveUserProfileSettings(
        email,
        viewKey,
        context,
        settingsArray
    );

    return Array.isArray(saved) ? [...saved] : [...settingsArray];
}

/**
 * Build + merge + save visible columns setting.
 */
export async function saveVisibleColumnsRuntime({
                                                    currentSettings = [],
                                                    columnDefinitions = [],
                                                    viewKey,
                                                    context = null,
                                                    tableViewType = "DEFAULT"
                                                }) {


    let visibleColumnsSetting = null;
    if(tableViewType === "GROUPED"){
        const existingSetting = findSettingByKey(currentSettings, "VISIBLE_COLUMNS_GROUPED");
        console.log("VISIBLE_COLUMNS_GROUPED saving", columnDefinitions)
        visibleColumnsSetting = buildVisibleColumnsGroupedSetting(
            columnDefinitions,
            viewKey,
            context,
            existingSetting?.id ?? null
        );
    }else if(tableViewType === "GROUPED_GRID"){
        const existingSetting = findSettingByKey(currentSettings, "VISIBLE_COLUMNS_GROUPED_GRID");

        visibleColumnsSetting = buildVisibleColumnsGroupedGridSetting(
            columnDefinitions,
            viewKey,
            context,
            existingSetting?.id ?? null
        );
    }else {
        const existingSetting = findSettingByKey(currentSettings, "VISIBLE_COLUMNS");

        visibleColumnsSetting = buildVisibleColumnsSetting(
            columnDefinitions,
            viewKey,
            context,
            existingSetting?.id ?? null
        );
    }



    const updatedSettings = mergeSettingIntoArray(currentSettings, visibleColumnsSetting);
    const savedSettings = await saveUserProfileSettingsRuntime({
        viewKey,
        context,
        settingsArray: updatedSettings,
    });

    return {
        savedSettings,
        savedVisibleColumns: [...columnDefinitions],
    };
}

/**
 * Build + merge + save sorting setting.
 */
export async function saveSortingSettingRuntime({
                                                    currentSettings = [],
                                                    sortColumn,
                                                    sortDirection,
                                                    viewKey,
                                                    context = null,
                                                }) {
    const existingSetting = findSettingByKey(currentSettings, "TABLE_SORTING");

    const sortingSetting = buildSortingSetting(
        {
            column: sortColumn,
            direction: sortDirection,
        },
        viewKey,
        context,
        existingSetting?.id ?? null
    );

    const updatedSettings = mergeSettingIntoArray(currentSettings, sortingSetting);
    const savedSettings = await saveUserProfileSettingsRuntime({
        viewKey,
        context,
        settingsArray: updatedSettings,
    });

    return {
        savedSettings,
        savedSorting: {
            column: sortColumn,
            direction: sortDirection,
        },
    };
}


export async function saveGroupingViewSettingRuntime({
                                                    currentSettings = [],
                                                         groupViewMode,
                                                         drugstoresInGridView,
                                                    viewKey,
                                                    context = null,
                                                }) {
    const existingSetting = findSettingByKey(currentSettings, "PACKAGE_ORDER_ITEMS_GROUPING_VIEW");

    const sortingSetting = buildPackageOrderGroupingViewSetting(
        {
            groupViewMode: groupViewMode,
            drugstoresInGridView: drugstoresInGridView,
        },
        viewKey,
        context,
        existingSetting?.id ?? null
    );

    const updatedSettings = mergeSettingIntoArray(currentSettings, sortingSetting);
    const savedSettings = await saveUserProfileSettingsRuntime({
        viewKey,
        context,
        settingsArray: updatedSettings,
    });

    return {
        savedSettings,
        savedGroupingView: {
            groupViewMode: groupViewMode,
            drugstoresInGridView: drugstoresInGridView,
        },
    };
}

/**
 * Utility for common sort toggle flow.
 */
export function resolveNextSortingState(currentSortingKey, currentSortingDirection, clickedSortKey) {
    let nextDirection = "ASC";

    if (currentSortingKey === clickedSortKey) {
        nextDirection = currentSortingDirection === "ASC" ? "DESC" : "ASC";
    } else {
        nextDirection = "ASC";
    }

    return {
        sortingKey: clickedSortKey,
        sortingDirection: nextDirection,
    };
}


